import { useEffect, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useTranslation } from 'react-i18next'
import { History, Loader2, Plus, Search, Send, ThumbsDown, ThumbsUp, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiClient } from '@/lib/api/client'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  streamAssistantAnswer, useAssistantConversationMutations, useAssistantConversations,
} from '../api'
import { AssistantMarkdown } from './AssistantMarkdown'
import { ProposalCard } from './ProposalCard'
import { toolLabel } from '../types'
import type {
  AssistantConversationDetail, AssistantProposalView, AssistantStreamEvent,
} from '../types'

/** One bubble in the transcript, as this panel holds it while the turn is in flight. */
interface Turn {
  role: 'USER' | 'ASSISTANT'
  text: string
  /** Tools consulted during this turn, in order, for the "looked at" line. */
  tools: string[]
  proposals: AssistantProposalView[]
  error?: string
}

/**
 * The assistant, as a slide-over from the top bar.
 *
 * <p>A panel rather than a page, because every question it answers is about the screen the
 * person is already on — "how much leave do I have" while looking at the leave calendar. A
 * route would have made them leave it.
 *
 * <p>Radix owns the focus trap, the Escape key and the `aria-modal` wiring. Written by hand,
 * a slide-over reliably loses one of the three, and the one it loses is usually focus return.
 */
export function AssistantPanel({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const [question, setQuestion] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const abort = useRef<AbortController | null>(null)
  const transcript = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLTextAreaElement>(null)

  const history = useAssistantConversations(open && showHistory)
  const { feedback, remove } = useAssistantConversationMutations()

  // Follows the answer as it is written. Only while it is being written: yanking the view
  // back down while somebody is rereading an earlier answer is worse than not following.
  useEffect(() => {
    if (streaming) transcript.current?.scrollTo({ top: transcript.current.scrollHeight })
  }, [turns, streaming])

  useEffect(() => {
    if (open) {
      // The panel exists to be typed into, so it opens with the caret already there.
      const timer = window.setTimeout(() => input.current?.focus(), 50)
      return () => window.clearTimeout(timer)
    }
    // Leaving the panel abandons the stream. The turn is already recorded server-side.
    abort.current?.abort()
    return undefined
  }, [open])

  async function ask() {
    const asked = question.trim()
    if (!asked || streaming) return

    setQuestion('')
    setStreaming(true)
    setTurns((previous) => [
      ...previous,
      { role: 'USER', text: asked, tools: [], proposals: [] },
      { role: 'ASSISTANT', text: '', tools: [], proposals: [] },
    ])

    const controller = new AbortController()
    abort.current = controller

    try {
      await streamAssistantAnswer(
        { conversationId, question: asked },
        (event) => apply(event),
        controller.signal,
      )
    } catch (failure) {
      const reason = failure instanceof Error ? failure.message : t('assistant.failed')
      // An abort is the person closing the panel, not a failure to report back to them.
      if (!controller.signal.aborted) {
        setTurns((previous) => patchLast(previous, (turn) => ({ ...turn, error: reason })))
      }
    } finally {
      setStreaming(false)
      abort.current = null
    }
  }

  function apply(event: AssistantStreamEvent) {
    setTurns((previous) => {
      switch (event.type) {
        case 'text':
          return patchLast(previous, (turn) => ({ ...turn, text: turn.text + event.delta }))
        case 'toolstarted':
          return patchLast(previous, (turn) => ({ ...turn, tools: [...turn.tools, event.tool] }))
        case 'proposal':
          return patchLast(previous, (turn) => ({
            ...turn,
            proposals: [...turn.proposals, {
              id: event.proposalId,
              toolName: event.tool,
              arguments: {},
              summary: event.summary,
              status: 'PENDING',
              expiresAt: null,
              resultEntityType: null,
              resultEntityId: null,
              refusal: null,
            }],
          }))
        case 'failed':
          return patchLast(previous, (turn) => ({ ...turn, error: event.reason }))
        default:
          return previous
      }
    })
    if (event.type === 'done') setConversationId(event.conversationId)
  }

  function startNew() {
    abort.current?.abort()
    setConversationId(null)
    setTurns([])
    setShowHistory(false)
    input.current?.focus()
  }

  /**
   * Reopens a past conversation from the server's record.
   *
   * <p>What comes back is the text of every turn and which tools were consulted — not what
   * those tools returned. Results are hashed, never stored, so an old conversation can say
   * "looked at your leave balance" and cannot show last month's number as if it were today's.
   */
  async function openConversation(id: string) {
    setShowHistory(false)
    setConversationId(id)
    setTurns([])

    const { data } = await apiClient.get<AssistantConversationDetail>(
      `/v1/assistant/conversations/${id}`)

    setTurns(data.messages
      .filter((message) => message.role === 'USER' || message.role === 'ASSISTANT')
      .map((message) => ({
        role: message.role as 'USER' | 'ASSISTANT',
        text: message.content ?? '',
        // The recorded tool calls are free-form JSON, so `name` is only a name when it is
        // actually a string; anything else is skipped rather than rendered as [object Object].
        tools: (message.toolCalls ?? [])
          .map((call) => (typeof call.name === 'string' ? call.name : ''))
          .filter(Boolean),
        proposals: [],
      })))
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed inset-y-0 end-0 z-50 flex w-[min(30rem,100vw)] flex-col',
            'border-s border-[var(--color-border)] bg-[var(--color-card)] shadow-[var(--shadow-popover)]',
          )}
        >
          <header className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-4 py-3">
            <Dialog.Title className="flex-1 text-base font-semibold text-[var(--color-heading)]">
              {t('assistant.title')}
            </Dialog.Title>
            <Button variant="ghost" size="icon" aria-label={t('assistant.newChat')} onClick={startNew}>
              <Plus className="size-4" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('assistant.history')}
              aria-pressed={showHistory}
              onClick={() => setShowHistory((shown) => !shown)}
            >
              <History className="size-4" aria-hidden="true" />
            </Button>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label={t('common.close')}>
                <X className="size-4" aria-hidden="true" />
              </Button>
            </Dialog.Close>
          </header>

          {showHistory ? (
            <div className="flex-1 overflow-y-auto overscroll-contain p-3">
              {history.isLoading && <p className="text-sm text-[var(--color-muted)]">{t('common.loading')}</p>}
              {history.data?.length === 0 && (
                <p className="text-sm text-[var(--color-muted)]">{t('assistant.noHistory')}</p>
              )}
              <ul className="space-y-1">
                {history.data?.map((conversation) => (
                  <li key={conversation.id} className="flex items-center gap-1">
                    <button
                      type="button"
                      className="min-w-0 flex-1 rounded-[var(--radius-control)] px-2 py-2 text-start text-sm hover:bg-[var(--color-page)]"
                      onClick={() => void openConversation(conversation.id)}
                    >
                      <span className="block truncate text-[var(--color-heading)]">
                        {conversation.title ?? t('assistant.untitled')}
                      </span>
                      <span className="block text-xs text-[var(--color-muted)]">
                        {conversation.messageCount} · {conversation.model ?? '—'}
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('assistant.deleteConversation')}
                      onClick={() => void remove.mutateAsync(conversation.id)}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div ref={transcript} className="flex-1 overflow-y-auto overscroll-contain p-3">
              {turns.length === 0 && <EmptyState onPick={(text) => setQuestion(text)} />}

              <ul className="space-y-3">
                {turns.map((turn, index) => (
                  // Index is the key on purpose: turns are only ever appended to, and the
                  // assistant's bubble has no id until the stream has finished.
                  <li key={index} className={turn.role === 'USER' ? 'flex justify-end' : ''}>
                    <div
                      className={cn(
                        'max-w-[85%] rounded-[var(--radius-card)] px-3 py-2 text-sm',
                        turn.role === 'USER'
                          ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
                          : 'bg-[var(--color-page)] text-[var(--color-body)]',
                      )}
                    >
                      {turn.tools.length > 0 && (
                        <p className="mb-1.5 flex items-start gap-1.5 text-xs text-[var(--color-muted)]">
                          <Search className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                          {t('assistant.lookedAt', {
                            what: turn.tools.map(toolLabel).join(', '),
                          })}
                        </p>
                      )}

                      {/*
                        Markdown for the assistant, plain text for the person.

                        A model writes markdown whether or not anything renders it, so the
                        assistant's half needs a renderer. What somebody typed is what they
                        typed: running a person's own question through a markdown parser would
                        eat their underscores and asterisks and show them something they did
                        not write.
                      */}
                      {turn.text && (turn.role === 'ASSISTANT'
                        ? <AssistantMarkdown>{turn.text}</AssistantMarkdown>
                        : <p className="whitespace-pre-wrap">{turn.text}</p>)}

                      {!turn.text && turn.role === 'ASSISTANT' && !turn.error && (
                        <p className="flex items-center gap-2 text-[var(--color-muted)]">
                          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                          {t('assistant.thinking')}
                        </p>
                      )}

                      {turn.proposals.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {turn.proposals.map((proposal) => (
                            <ProposalCard key={proposal.id} proposal={proposal} />
                          ))}
                        </div>
                      )}

                      {turn.error && (
                        <p role="alert" className="mt-1 text-[var(--color-danger)]">{turn.error}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!showHistory && (
            <footer className="shrink-0 border-t border-[var(--color-border)] p-3">
              {conversationId && turns.length > 0 && (
                <div className="mb-2 flex items-center gap-1 text-xs text-[var(--color-muted)]">
                  <span>{t('assistant.wasThisUseful')}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('assistant.thumbsUp')}
                    onClick={() => void feedback.mutateAsync({ id: conversationId, feedback: 'UP' })}
                  >
                    <ThumbsUp className="size-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('assistant.thumbsDown')}
                    onClick={() => void feedback.mutateAsync({ id: conversationId, feedback: 'DOWN' })}
                  >
                    <ThumbsDown className="size-4" aria-hidden="true" />
                  </Button>
                </div>
              )}

              <form
                className="flex items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  void ask()
                }}
              >
                <Textarea
                  ref={input}
                  rows={2}
                  value={question}
                  aria-label={t('assistant.askLabel')}
                  placeholder={t('assistant.placeholder')}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={(event) => {
                    // Enter sends, Shift+Enter writes a new line — the convention every chat
                    // uses, and the one people try first.
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault()
                      void ask()
                    }
                  }}
                />
                <Button type="submit" size="icon" disabled={streaming || !question.trim()} aria-label={t('assistant.send')}>
                  {streaming
                    ? <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    : <Send className="size-4 rtl-flip" aria-hidden="true" />}
                </Button>
              </form>

              <p className="mt-2 text-xs text-[var(--color-muted)]">{t('assistant.disclaimer')}</p>
            </footer>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function EmptyState({ onPick }: { onPick: (text: string) => void }) {
  const { t } = useTranslation()
  const suggestions = t('assistant.suggestions', { returnObjects: true }) as string[]

  return (
    <div className="px-1 py-4">
      <p className="text-sm text-[var(--color-body)]">{t('assistant.intro')}</p>
      <ul className="mt-3 space-y-1.5">
        {(Array.isArray(suggestions) ? suggestions : []).map((suggestion) => (
          <li key={suggestion}>
            <button
              type="button"
              onClick={() => onPick(suggestion)}
              className="w-full rounded-[var(--radius-control)] border border-[var(--color-border)] px-3 py-2 text-start text-sm hover:bg-[var(--color-page)]"
            >
              {suggestion}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Replaces the last turn. The assistant's bubble is always the one being written into. */
function patchLast(turns: Turn[], change: (turn: Turn) => Turn): Turn[] {
  if (turns.length === 0) return turns
  return turns.map((turn, index) => (index === turns.length - 1 ? change(turn) : turn))
}
